import { useEffect, useState } from 'react'
import {
  Sparkles,
  Bot,
  Video,
  Hash,
  CheckSquare,
  AlertTriangle,
  Activity as ActivityIcon,
  ChevronRight,
  Power,
} from 'lucide-react'
import DashboardPage from './DashboardPage'
import { KpiCard, KpiRow, SectionCard } from '../../components/patterns'
import { Avatar, Pill, ThreeCol, TwoCol, VisionRibbon } from './widgets'
import api from '../../services/api'

interface CallSummaryDto {
  id: string
  source: string
  title: string
  whenAgo: string
  participants: number
  bullets: string[]
  decisions: string[]
  tasksCreated: number
}
interface AgentDto {
  id: string
  name: string
  status: 'on'
  description: string
  lastRun: string | null
  events30d: number
}
interface AiAlertDto {
  id: string
  agent: string
  severity: string
  title: string
  body: string
  whenAgo: string
}
interface AiDashboardData {
  kpis: { callsProcessed30d: number; tasksAutoCreated30d: number }
  callSummaries: CallSummaryDto[]
  agents: AgentDto[]
  aiAlerts: AiAlertDto[]
}

// Relative time from an ISO string (web-only; Date.now is fine here).
function ago(iso: string | null): string {
  if (!iso) return 'never'
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return '—'
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export default function AiDashboard() {
  const [data, setData] = useState<AiDashboardData | null>(null)

  useEffect(() => {
    api
      .get('/workspace/ai/dashboard')
      .then((r) => setData(r.data))
      .catch(() => setData(null))
  }, [])

  const callSummaries = data?.callSummaries ?? []
  const agents = data?.agents ?? []
  const aiAlerts = data?.aiAlerts ?? []

  return (
    <DashboardPage
      title="AI Layer"
      subtitle="Agents that listen to calls, chats and tasks — produce summaries, flag risks, draft follow-ups"
    >
      <VisionRibbon />

      <KpiRow className="md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label="Calls processed (30d)" value={data ? data.kpis.callsProcessed30d : '—'}   accent="text-foreground"   icon={<Video size={18} />} />
        <KpiCard label="Tasks auto-created"    value={data ? data.kpis.tasksAutoCreated30d : '—'} accent="text-emerald-400"  icon={<CheckSquare size={18} />} />
        <KpiCard label="Hours saved / week"    value="—"                                          accent="text-fuchsia-400"  icon={<Sparkles size={18} />} />
        <KpiCard label="Suggestions actioned"  value="—"                                          accent="text-sky-400"      icon={<ActivityIcon size={18} />} />
        <KpiCard label="Open alerts"           value={data ? aiAlerts.filter((a) => a.severity !== 'info').length : '—'} accent="text-rose-400" icon={<AlertTriangle size={18} />} />
      </KpiRow>

      <TwoCol>
        <SectionCard icon={<Video size={14} />} title="Recent call summaries" subtitle="Auto-generated from voice rooms" noBodyPadding>
          {callSummaries.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">
              No call summaries yet — they appear within a few minutes after a voice call is transcribed.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {callSummaries.map((c) => (
                <div key={c.id} className="px-5 py-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-muted-foreground">
                      {c.source === 'discord' ? <Hash size={14} /> : <Video size={14} />}
                    </span>
                    <span className="text-sm text-foreground font-semibold">{c.title}</span>
                    <span className="ml-auto text-[10px] text-muted-foreground">{ago(c.whenAgo)} · {c.participants}p</span>
                  </div>
                  {c.bullets.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {c.bullets.map((b, i) => (
                        <div key={i} className="text-xs text-muted-foreground flex items-start gap-1.5 leading-relaxed">
                          <span className="text-fuchsia-400 mt-1.5 w-1 h-1 rounded-full bg-fuchsia-400 shrink-0" />
                          <span>{b}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 pt-2 border-t border-border/60">
                    <span className="text-[10px] text-muted-foreground">Decisions:</span>
                    <span className="text-[10px] text-foreground truncate flex-1">
                      {c.decisions[0] ?? '—'}{c.decisions.length > 1 && ` +${c.decisions.length - 1}`}
                    </span>
                    <Pill tone="success">{c.tasksCreated} tasks</Pill>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard icon={<AlertTriangle size={14} />} title="Active alerts" subtitle="From all agents" noBodyPadding>
          {aiAlerts.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">
              No active alerts — all monitored signals are within normal range.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {aiAlerts.map((a) => (
                <div key={a.id} className="px-5 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Pill tone={a.severity === 'critical' ? 'danger' : a.severity === 'warn' ? 'warn' : 'info'}>{a.severity}</Pill>
                    <span className="text-[10px] uppercase tracking-wider text-fuchsia-400 font-semibold">{a.agent}</span>
                    <span className="ml-auto text-[10px] text-muted-foreground">{ago(a.whenAgo)}</span>
                  </div>
                  <div className="text-sm text-foreground font-medium">{a.title}</div>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-1">{a.body}</p>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </TwoCol>

      <SectionCard icon={<Bot size={14} />} title="AI agents" subtitle="Active prompts and their 30-day activity">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {agents.map((a) => (
            <div key={a.id} className="rounded-lg ring-1 ring-border bg-card p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-500/15 text-emerald-400">
                  <Bot size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground font-semibold truncate">{a.name}</div>
                  <div className="text-[10px] text-muted-foreground">last run {ago(a.lastRun)}</div>
                </div>
                <button
                  className="p-1 rounded transition text-emerald-400 hover:bg-emerald-500/10"
                  title="Active"
                >
                  <Power size={14} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{a.description}</p>
              <div className="flex items-center gap-2 pt-2 border-t border-border/60">
                <span className="text-[10px] text-muted-foreground tabular-nums">{a.events30d} events / 30d</span>
                <Pill tone="success">{a.status}</Pill>
                <button className="ml-auto text-[10px] text-muted-foreground hover:text-foreground transition flex items-center gap-1">
                  Configure <ChevronRight size={10} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <ThreeCol>
        <SectionCard icon={<Sparkles size={14} />} title="Pre-call brief preview" subtitle="What you get 10 min before a call">
          <div className="rounded-lg bg-muted/30 ring-1 ring-border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Avatar name="Quanta Bio" colourKey="amber" size={28} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-foreground font-semibold">Quanta Bio · Renewal</div>
                <div className="text-[10px] text-muted-foreground">in 8 min · Iryna Kovalenko</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1.5 leading-relaxed pt-2 border-t border-border/60">
              <div><span className="text-foreground font-medium">Last touch:</span> 2 days ago — proposed annual price</div>
              <div><span className="text-foreground font-medium">Open promise:</span> Send commit pricing (overdue 1d)</div>
              <div><span className="text-foreground font-medium">Risks:</span> Competitor mentioned in last call</div>
              <div><span className="text-foreground font-medium">Open tickets:</span> 1 (P3 — seat increase)</div>
            </div>
          </div>
        </SectionCard>

        <SectionCard icon={<CheckSquare size={14} />} title="Auto-extracted last 24h" subtitle="What AI captured for you">
          <div className="space-y-2.5 text-xs">
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /><span className="text-foreground">14 tasks created from chats</span></div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" /><span className="text-foreground">3 deals advanced (Closing → Won)</span></div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" /><span className="text-foreground">2 tickets escalated to P1 by tone</span></div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /><span className="text-foreground">1 customer flagged churn-risk</span></div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-rose-500" /><span className="text-foreground">1 SOP gap detected (GDPR DSR)</span></div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" /><span className="text-foreground">8 follow-up drafts ready to review</span></div>
          </div>
        </SectionCard>

        <SectionCard icon={<ActivityIcon size={14} />} title="Decision log (read-only)" subtitle="What AI decided + why">
          <div className="space-y-2.5">
            {[
              { what: 'Promoted Acme ticket P2 → P1',           why: 'Sentiment dropped, third complaint in 48h' },
              { what: 'Created task for Iryna from Polaris call', why: 'Iryna verbally committed on recording' },
              { what: 'Flagged BlueRiver as unprofitable',        why: '36h support / month vs $7.9k MRR' },
            ].map((d, i) => (
              <div key={i} className="text-xs">
                <div className="text-foreground">{d.what}</div>
                <div className="text-[10px] text-muted-foreground italic">↳ {d.why}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      </ThreeCol>
    </DashboardPage>
  )
}
